import { useCallback, useEffect, useState } from "react";
import { Button, Message } from "@humansignal/ui";
import { useAPI } from "@humansignal/core";
import { useAuth } from "@humansignal/core/providers/AuthProvider";
import { SafeMarkdown } from "../SafeMarkdown/SafeMarkdown";
import { AFRICAN_COUNTRIES, OTHER_COUNTRIES } from "./countries";
import { useTranslation } from "../../i18n/LocaleProvider";

// Static — neither list changes at runtime, so this lives outside the
// component to avoid the previous useMemo-after-early-return bug
// that produced React #310 (hook count changed between renders
// because the memo sat below `if (pending === null) return …`).
const COUNTRY_OPTION_GROUPS: { group: string; entries: [string, string][] }[] = [
  { group: "African Union members", entries: AFRICAN_COUNTRIES },
  {
    group: "Other countries",
    entries: OTHER_COUNTRIES.slice().sort((a, b) => a[1].localeCompare(b[1])),
  },
];

/**
 * ConsentGate — blocks rendering of its children until the current
 * user has signed every required ConsentTemplate for the project.
 *
 * Flow:
 *   1. On mount: GET /api/consent/projects/<id>/pending/
 *   2. Empty pending list → render children immediately.
 *   3. Non-empty → render a fullscreen overlay walking each pending
 *      requirement: SafeMarkdown body, "I agree" checkbox, typed-name
 *      input, "Sign and continue" button. Optional rows have a
 *      "Skip for now" button that doesn't POST a signature.
 *   4. On the last requirement, POST /api/consent/signatures/ for it
 *      and re-fetch /pending/ to confirm the gate clears (server is
 *      the source of truth — re-fetch defeats any client-side state
 *      drift; if it doesn't clear, the user stays gated).
 *
 * Two design choices worth flagging:
 *   * The gate is the LAST layer before the labeling UI mounts, not
 *     the first. The server-side check in tasks/api.py is the actual
 *     enforcement point — the gate is UX. A 403 from a direct API
 *     POST will surface the same `consent_required` payload the gate
 *     consumes.
 *   * We render the overlay UNDER the page's normal chrome (the
 *     surrounding nav / breadcrumb / header still show), so the
 *     annotator sees where they are. They just can't interact with
 *     the labeling-specific UI underneath. The overlay is dismissed
 *     by signing, never by clicking outside or pressing Escape.
 */

interface ConsentPurpose {
  key: string;
  label: string;
  description?: string;
  required?: boolean;
}

interface PendingRow {
  requirement_id: number;
  optional: boolean;
  already_signed_version: number | null;
  template: {
    id: number;
    title: string;
    kind: string;
    body: string;
    version: number;
    purposes?: ConsentPurpose[];
  };
}

export const ConsentGate = ({
  projectId,
  children,
}: {
  projectId: number | string | null | undefined;
  children: React.ReactNode;
}) => {
  const { t } = useTranslation();
  const api = useAPI();
  const { user, refetch: refetchUser } = useAuth() as {
    user: { id?: number | string; first_name?: string; last_name?: string; email?: string } | null;
    refetch: () => void;
  };
  // Profile name is the legally-attributed identity for THIS user.
  // We pin the typed-name field to it (read-only) when set, so a
  // determined annotator can't sign as "Mickey Mouse" and weaken
  // their own signature's evidentiary value. When the profile is
  // empty (annotator hasn't filled their name yet) we fall back to
  // a free-text input but warn that the typed name should match
  // the legal name they would be invoiced / paid under.
  const profileName = user ? [user.first_name, user.last_name].filter(Boolean).join(" ").trim() : "";
  const [pending, setPending] = useState<PendingRow[] | null>(null);
  // Reflects /pending/ ``profile_complete`` flag. Null = not yet
  // probed. False = the SPA should show the profile-completion gate
  // BEFORE the consent gate — a missing first/last name on the
  // user's profile means consent attribution would be empty.
  const [profileComplete, setProfileComplete] = useState<boolean | null>(null);
  const [savingProfile, setSavingProfile] = useState(false);
  // One "full legal name" field — NOT split first/last. Many legal
  // names don't fit a Western given/family shape (mononyms; Ethiopian
  // given-name + father's-name), and forcing a second field made those
  // users invent one — fake data that weakens, not strengthens, the
  // consent attribution. The signature reproduces the name verbatim.
  const [profileFullName, setProfileFullName] = useState("");
  const [stepIdx, setStepIdx] = useState(0);
  const [agreed, setAgreed] = useState(false);
  // ``authorityConfirmed`` — separate checkbox for the explicit
  // affirmation that the signer has authority to sign this consent
  // (themselves, or as a guardian). Required by GDPR Art. 7(1)
  // ("controller shall be able to demonstrate that the data subject
  // has consented") + parallel POPIA / NDPA / Kenya-DPA clauses.
  const [authorityConfirmed, setAuthorityConfirmed] = useState(false);
  const [signedName, setSignedName] = useState(profileName);
  // Set of purpose keys the user has ticked. Cleared between steps
  // so each consent step starts fresh — purpose acceptance from
  // template A doesn't leak into template B.
  const [purposesAccepted, setPurposesAccepted] = useState<Set<string>>(() => new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Country + age + minor name are captured ONCE per session, NOT
  // re-asked for every consent step. Different jurisdictions and
  // ages can't apply to the same signing session — those are
  // properties of the human, not of the document.
  const [country, setCountry] = useState("");
  const [age, setAge] = useState<"" | "18_or_over" | "under_18_with_guardian" | "guardian_signing">("");
  const [minorName, setMinorName] = useState("");

  const refetch = useCallback(async () => {
    if (!projectId) return;
    setError("");
    const r = await api.callApi("consentPending", { params: { project_id: projectId } });
    // Fail-open at the GATE level — if the network breaks the user
    // can still try to label. Server-side enforcement on submit
    // remains, so an unsigned annotator will be blocked by the
    // 403 from the actual save attempt instead of being blocked
    // here on a flaky API.
    if (r?.error) {
      setPending([]);
      setProfileComplete(true); // don't block on errors
      return;
    }
    setProfileComplete(r?.profile_complete !== false);
    setPending(Array.isArray(r?.pending) ? r.pending : []);
  }, [api, projectId]);

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Re-fetch on tab focus / visibility / bfcache restore so the gate
  // re-asserts itself when the annotator returns to this page after
  // doing something elsewhere — most importantly after withdrawing
  // a consent on /users/<id>/consents and navigating back.
  // Without this, browsers' back-forward cache can restore the gate
  // with its previous (empty) ``pending`` snapshot and let the
  // labeling UI through even though the user just revoked the
  // consent that allowed it. Server-side ``assert_consents_signed``
  // would still 403 the next save, but the UX without a re-fetch
  // here was "labeling page works, save fails" — better to surface
  // the gate immediately.
  useEffect(() => {
    const onVisible = () => {
      if (typeof document !== "undefined" && document.visibilityState === "visible") {
        refetch();
      }
    };
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) refetch();
    };
    const onFocus = () => refetch();
    if (typeof document !== "undefined") document.addEventListener("visibilitychange", onVisible);
    if (typeof window !== "undefined") {
      window.addEventListener("pageshow", onPageShow);
      window.addEventListener("focus", onFocus);
    }
    return () => {
      if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVisible);
      if (typeof window !== "undefined") {
        window.removeEventListener("pageshow", onPageShow);
        window.removeEventListener("focus", onFocus);
      }
    };
  }, [refetch]);

  // We ALWAYS render children, even while loading or while the gate
  // modal is open. The previous design returned only the overlay
  // when blocked, which meant the DataManager's mount node never
  // existed during the gate; once the gate cleared, ``init()`` had
  // already run with a null root and bailed, leaving the labeling
  // UI permanently blank. Rendering children unconditionally lets
  // the DataManager initialize behind the modal so the labeling
  // surface is ready the moment the gate closes. Server-side
  // ``assert_consents_signed`` is the actual security boundary —
  // mounting the UI doesn't bypass it; any annotation save still
  // 403s on an unsigned annotator.
  const showLoadingOverlay = pending === null;
  const showProfileOverlay = profileComplete === false;
  // Everyone confirms their legal name once. So when the profile is
  // incomplete: a name already on file (one part or two) means this is
  // the "confirm your name" case (pre-fill + re-label); an empty
  // profile is the "collect" case. The field stays editable either way
  // so the user can correct the name instead of just confirming it.
  const confirmExistingName = showProfileOverlay && !!profileName;
  // Consent overlay only fires once profile is complete — otherwise
  // signing would attribute to an empty name and weaken evidence.
  const showModalOverlay = profileComplete !== false && pending !== null && pending.length > 0;

  const onSaveProfile = async () => {
    const name = profileFullName.trim();
    if (name.length < 2) {
      setError("Enter your full legal name to continue.");
      return;
    }
    setSavingProfile(true);
    // Two cases:
    //  * CONFIRM unchanged — the name already on file is correct. Only
    //    flip ``legal_name_confirmed``; DON'T rewrite first/last, so an
    //    existing "First Last" split (and its initials) is preserved.
    //  * EDIT — the user corrected the name. Store it verbatim in
    //    ``first_name`` and clear ``last_name``: we don't guess a split,
    //    the name must reproduce exactly as given on the signature.
    const unchanged = name === profileName.trim();
    const body = unchanged
      ? { legal_name_confirmed: true }
      : { first_name: name, last_name: "", legal_name_confirmed: true };
    const r = await api.callApi("updateUser", {
      params: { pk: user?.id ?? "me" },
      body,
    });
    setSavingProfile(false);
    if (r?.error) {
      setError(r.response?.detail || "Could not save your profile.");
      return;
    }
    // The consent signature attributes to ``profileName``, which is
    // derived from the ``useAuth()`` user. That user object is loaded
    // once and is now STALE (it still holds the pre-save name, e.g. a
    // first name with no last name) — so without refreshing it the
    // consent step would sign under the OLD name even though the user
    // just corrected it here. Refresh the canonical auth user so
    // ``profileName`` (and every other consumer) reflects the new
    // first+last, and seed ``signedName`` immediately so the read-only
    // consent field is right on the very next render, before the
    // refetch round-trips.
    setSignedName(name);
    try {
      await refetchUser?.();
    } catch {
      /* non-fatal — the sync effect below still corrects signedName
         once the auth user updates; the server has the new name. */
    }
    // Re-probe /pending/. The response will flip
    // ``profile_complete`` to true and either render the consent
    // gate next or fall through to children. We don't trust the
    // local state ("I just typed it") because the server is the
    // authority on what's persisted.
    await refetch();
  };

  // Keep the consent signature name in lockstep with the profile name.
  // ``signedName`` is seeded from ``profileName`` at mount, but the
  // profile can change mid-session (the profile-completion gate above,
  // or an edit in another tab that a focus-refetch picks up). Whenever
  // a non-empty profile name lands, mirror it into ``signedName`` — the
  // field is read-only while a profile name exists, so this never
  // clobbers free-text the user typed when they had no name on file.
  useEffect(() => {
    if (profileName) setSignedName(profileName);
  }, [profileName]);

  // Pre-fill the profile field with the single name already on file
  // when we're in the "confirm your single name" case, so the user can
  // confirm it as-is or edit it. Only seed while the field is still
  // empty so we don't stomp on what they're typing.
  useEffect(() => {
    if (confirmExistingName && profileName && !profileFullName) {
      setProfileFullName(profileName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmExistingName, profileName]);

  // ``idx``, ``current``, and the surrounding modal-only state are
  // safe to access regardless of pending state since the JSX block
  // that uses them is conditioned on ``showModalOverlay``. When
  // there's no modal we just don't render that subtree.
  const safePending = pending ?? [];
  const idx = safePending.length === 0 ? 0 : Math.min(stepIdx, safePending.length - 1);
  const current = safePending[idx];
  const tpl = current?.template;
  const required = current ? !current.optional : false;
  const isUpgrade = current ? current.already_signed_version !== null : false;

  const advance = async () => {
    setAgreed(false);
    setAuthorityConfirmed(false);
    setSignedName(profileName);
    setPurposesAccepted(new Set());
    setError("");
    if (idx + 1 < pending.length) {
      setStepIdx(idx + 1);
      return;
    }
    // Last step — re-fetch and either clear (renders children) or
    // restart at idx 0 with whatever's still unmet.
    await refetch();
    setStepIdx(0);
  };

  const onSign = async () => {
    if (!country) {
      setError("Choose your country of residence, different jurisdictions grant different rights.");
      return;
    }
    if (!age) {
      setError("Confirm your age category before signing.");
      return;
    }
    if ((age === "under_18_with_guardian" || age === "guardian_signing") && !minorName.trim()) {
      setError("Type the minor's full name when a guardian is signing.");
      return;
    }
    if (!agreed) {
      setError("Please tick the box confirming you have read and agree.");
      return;
    }
    if (!authorityConfirmed) {
      setError("Confirm that you have authority to sign this consent.");
      return;
    }
    if (!signedName.trim() || signedName.trim().length < 2) {
      setError("Type your full legal name to sign.");
      return;
    }
    // Soft check — typed name should match the profile name on
    // file. This is a usability nudge, not an enforcement gate;
    // the legal binding is the authenticated session, not the
    // string. We let the user proceed so a profile that's missing
    // a last name doesn't trap them, but record the mismatch by
    // annotating it in the error/warning UI.
    if (profileName && signedName.trim().toLowerCase() !== profileName.toLowerCase()) {
      // Hard-block here because the field is read-only when
      // profile is set; getting here means the user altered it
      // through devtools or the profile changed mid-session.
      setError(
        `Typed name must match the name on your profile (${profileName}). Update your profile if it's wrong, then try again.`,
      );
      return;
    }
    // Per-purpose validation. Required purposes must each be ticked.
    const purposes = (tpl?.purposes || []) as ConsentPurpose[];
    const missingRequired = purposes
      .filter((p) => p.required && !purposesAccepted.has(p.key))
      .map((p) => p.label || p.key);
    if (missingRequired.length) {
      setError(
        `Tick the required purpose${missingRequired.length === 1 ? "" : "s"} before signing: ${missingRequired.join(", ")}.`,
      );
      return;
    }
    setBusy(true);
    const r = await api.callApi("addConsentSignature", {
      body: {
        template: tpl.id,
        signed_name: signedName.trim(),
        project: projectId ?? null,
        country_of_residence: country,
        age_attestation: age,
        minor_name: age === "under_18_with_guardian" || age === "guardian_signing" ? minorName.trim() : "",
        purposes_accepted: Array.from(purposesAccepted),
      },
    });
    setBusy(false);
    if (r?.error) {
      setError(r.response?.detail || r.response?.message || "Could not record signature.");
      return;
    }
    await advance();
  };

  const onSkip = async () => {
    if (required) return;
    await advance();
  };

  return (
    <>
      {children}

      {showLoadingOverlay ? (
        <div
          aria-live="polite"
          aria-busy="true"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            background: "rgba(255, 255, 255, 0.65)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        />
      ) : null}

      {showProfileOverlay ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="profile-gate-title"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            background: "rgba(0, 0, 0, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
            overflowY: "auto",
          }}
        >
          <div
            style={{
              background: "var(--color-neutral-background, #fff)",
              borderRadius: 12,
              width: "100%",
              maxWidth: 540,
              padding: "24px 28px",
              boxShadow: "0 12px 48px rgba(0, 0, 0, 0.25)",
            }}
          >
            <h2
              id="profile-gate-title"
              style={{ margin: "0 0 8px", fontSize: "var(--font-size-title-md)", fontWeight: 600 }}
            >
              {confirmExistingName ? "Confirm your full legal name" : "Complete your profile to continue"}
            </h2>
            <p
              style={{
                margin: "0 0 16px",
                fontSize: "var(--font-size-body-small)",
                lineHeight: 1.5,
                color: "var(--color-neutral-content)",
              }}
            >
              {confirmExistingName ? (
                <>
                  Confirm the name we have on file is your full legal name as it appears on your official ID — or
                  correct it below. This is what we attribute every consent signature to, and what your organization
                  owner verifies before adding you to projects. You only need to do this once.
                </>
              ) : (
                <>
                  Add your full legal name before joining projects or signing consent agreements. Your name is what we
                  attribute every consent signature to and what your organization owner will verify against their own
                  records before adding you to projects.
                </>
              )}
            </p>
            {error ? (
              <Message variant="negative" size="small">
                {error}
              </Message>
            ) : null}
            <div style={{ marginTop: 12 }}>
              <label
                style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: "var(--font-size-body-small)" }}
              >
                <span>Full legal name</span>
                <input
                  type="text"
                  value={profileFullName}
                  onChange={(e) => setProfileFullName(e.target.value)}
                  disabled={savingProfile}
                  autoComplete="name"
                  placeholder={t("components.consentgate.consent_gate.eg_idris_abdulmumin")}
                  style={{
                    padding: "8px 12px",
                    border: "1px solid var(--color-neutral-border, rgba(0,0,0,0.15))",
                    borderRadius: 6,
                    background: "var(--color-neutral-background, #fff)",
                    fontSize: "var(--font-size-body-small)",
                  }}
                />
              </label>
            </div>
            <p
              style={{
                margin: "12px 0 0",
                fontSize: "var(--font-size-caption)",
                color: "var(--color-neutral-content-subtler)",
                lineHeight: 1.45,
              }}
            >
              Use the legal name on your government ID. Pseudonyms or partial names weaken consent enforceability in a
              dispute.
            </p>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
              <Button
                variant="primary"
                look="filled"
                size="small"
                onClick={onSaveProfile}
                waiting={savingProfile}
                disabled={savingProfile}
              >
                {confirmExistingName ? "Confirm and continue" : "Save and continue"}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {showModalOverlay && current && tpl ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="consent-gate-title"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            background: "rgba(0, 0, 0, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
            overflowY: "auto",
          }}
        >
          <div
            style={{
              background: "var(--color-neutral-background, #fff)",
              borderRadius: 12,
              width: "100%",
              maxWidth: 720,
              maxHeight: "90vh",
              // minHeight:0 lets this flex column shrink to the viewport cap;
              // overflow:hidden clips children to the rounded box so nothing
              // (long markdown, a tall footer) can escape OUTSIDE the modal
              // on Windows, where reserved scrollbars + wider font metrics
              // push the content past the 90vh cap. The body below is the
              // scroll region. See win-browser rendering fixes.
              minHeight: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 12px 48px rgba(0, 0, 0, 0.25)",
            }}
          >
            <header
              style={{
                padding: "20px 24px 12px",
                borderBottom: "1px solid var(--color-neutral-border-subtle, rgba(0,0,0,0.08))",
                flexShrink: 0,
                minWidth: 0,
              }}
            >
              <div
                style={{
                  fontSize: "var(--font-size-caption)",
                  color: "var(--color-neutral-content-subtler, #6b6b6b)",
                  textTransform: "uppercase",
                  letterSpacing: 0.04,
                }}
              >
                Consent {idx + 1} of {pending.length}
                {required ? null : <> {t("components.consentgate.consent_gate.optional")}</>}
                {isUpgrade ? <>, updated to v{tpl.version}</> : null}
              </div>
              <h2
                id="consent-gate-title"
                style={{ margin: "4px 0 0", fontSize: "var(--font-size-title-md)", fontWeight: 600 }}
              >
                {tpl.title}
              </h2>
            </header>

            <div
              style={{
                padding: "16px 24px",
                overflowY: "auto",
                flex: "1 1 auto",
                // minHeight:0 is what actually lets a flex child scroll instead
                // of growing to its content height and pushing the footer (and
                // itself) outside the modal — the classic Windows overflow.
                // minWidth:0 + overflowWrap break long unbreakable strings/URLs
                // in the consent body so they wrap rather than force sideways
                // overflow.
                minHeight: 0,
                minWidth: 0,
                overflowWrap: "break-word",
              }}
            >
              <SafeMarkdown text={tpl.body} />
            </div>

            <footer
              style={{
                padding: "16px 24px",
                borderTop: "1px solid var(--color-neutral-border-subtle, rgba(0,0,0,0.08))",
                display: "flex",
                flexDirection: "column",
                gap: 12,
                // Keep the sign controls at their natural height (don't let them
                // get squeezed) and let the body above absorb the scroll.
                // minWidth:0 lets the inner form fields shrink instead of
                // forcing horizontal overflow. If the footer itself ever
                // exceeds the space left under the cap it scrolls internally
                // rather than escaping the modal.
                flexShrink: 0,
                minWidth: 0,
                maxHeight: "60vh",
                overflowY: "auto",
              }}
            >
              {error ? (
                <Message variant="negative" size="small">
                  {error}
                </Message>
              ) : null}

              {/* Country + age are asked once on the FIRST step (idx 0)
              and persist across steps. Different jurisdictions grant
              different data-subject rights, and ages can't change
              between two consents signed in the same minute — so
              capturing once is correct + less friction. */}
              {idx === 0 ? (
                <>
                  <label
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                      fontSize: "var(--font-size-body-small)",
                    }}
                  >
                    <span>{t("components.consentgate.consent_gate.country_of_residence")}</span>
                    <select
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      disabled={busy}
                      style={{
                        padding: "8px 12px",
                        border: "1px solid var(--color-neutral-border, rgba(0,0,0,0.15))",
                        borderRadius: 6,
                        background: "var(--color-neutral-background, #fff)",
                        fontSize: "var(--font-size-body-small)",
                      }}
                    >
                      <option value="">{t("components.consentgate.consent_gate.select")}</option>
                      {COUNTRY_OPTION_GROUPS.map((g) => (
                        <optgroup key={g.group} label={g.group}>
                          {g.entries.map(([code, name]) => (
                            <option key={code} value={code}>
                              {name}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </label>

                  <fieldset
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                      border: 0,
                      padding: 0,
                      margin: 0,
                      fontSize: "var(--font-size-body-small)",
                    }}
                  >
                    <legend style={{ fontSize: "var(--font-size-body-small)", padding: 0 }}>
                      {t("components.consentgate.consent_gate.age_confirm_one")}
                    </legend>
                    {[
                      ["18_or_over", "I am 18 or older."],
                      [
                        "under_18_with_guardian",
                        "I am under 18 and my legal guardian has consented to my participation.",
                      ],
                      ["guardian_signing", "I am signing on behalf of a minor as their legal guardian."],
                    ].map(([k, label]) => (
                      <label key={k} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                        <input
                          type="radio"
                          name="age"
                          value={k}
                          checked={age === k}
                          onChange={() => setAge(k as typeof age)}
                          disabled={busy}
                          style={{ marginTop: 3 }}
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                  </fieldset>

                  {age === "under_18_with_guardian" || age === "guardian_signing" ? (
                    <label
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                        fontSize: "var(--font-size-body-small)",
                      }}
                    >
                      <span>
                        {age === "guardian_signing" ? "Minor's full name" : "Minor's full name (the annotator)"}
                      </span>
                      <input
                        type="text"
                        value={minorName}
                        onChange={(e) => setMinorName(e.target.value)}
                        disabled={busy}
                        style={{
                          padding: "8px 12px",
                          border: "1px solid var(--color-neutral-border, rgba(0,0,0,0.15))",
                          borderRadius: 6,
                          background: "var(--color-neutral-background, #fff)",
                          fontSize: "var(--font-size-body-small)",
                        }}
                      />
                    </label>
                  ) : null}
                </>
              ) : null}

              <label
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "flex-start",
                  fontSize: "var(--font-size-body-small)",
                  lineHeight: 1.4,
                }}
              >
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  disabled={busy}
                  style={{ marginTop: 3 }}
                />
                <span>
                  I confirm I have read this document and I agree to the processing it describes. I am signing freely,
                  voluntarily, and unambiguously.
                </span>
              </label>

              {/* Per-purpose granular consent — GDPR Art. 7(2). Each
              listed purpose gets its own checkbox so the user can
              opt in to specific processing activities independently
              of the overall agreement. Required purposes are
              flagged with an asterisk; the server rejects the
              signature if any required purpose isn't ticked. */}
              {tpl?.purposes && tpl.purposes.length > 0 ? (
                <fieldset
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    border: "1px solid var(--color-neutral-border-subtle, rgba(0,0,0,0.08))",
                    borderRadius: 6,
                    padding: "10px 12px",
                    margin: 0,
                    fontSize: "var(--font-size-body-small)",
                  }}
                >
                  <legend style={{ fontSize: "var(--font-size-body-small)", fontWeight: 600, padding: "0 6px" }}>
                    I agree to these specific purposes
                  </legend>
                  {tpl.purposes.map((p) => {
                    const checked = purposesAccepted.has(p.key);
                    return (
                      <label
                        key={p.key}
                        style={{ display: "flex", gap: 10, alignItems: "flex-start", lineHeight: 1.45 }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            setPurposesAccepted((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(p.key);
                              else next.delete(p.key);
                              return next;
                            });
                          }}
                          disabled={busy}
                          style={{ marginTop: 3 }}
                        />
                        <span>
                          <span style={{ fontWeight: 500 }}>
                            {p.label || p.key}
                            {p.required ? (
                              <span aria-hidden style={{ color: "var(--color-negative-content)", marginLeft: 4 }}>
                                *
                              </span>
                            ) : null}
                          </span>
                          {p.description ? (
                            <div
                              style={{
                                fontSize: "var(--font-size-caption)",
                                color: "var(--color-neutral-content-subtler)",
                                marginTop: 2,
                              }}
                            >
                              {p.description}
                            </div>
                          ) : null}
                        </span>
                      </label>
                    );
                  })}
                  {tpl.purposes.some((p) => p.required) ? (
                    <div
                      style={{ fontSize: "var(--font-size-caption)", color: "var(--color-neutral-content-subtler)" }}
                    >
                      <span aria-hidden style={{ color: "var(--color-negative-content)" }}>
                        *
                      </span>{" "}
                      required to sign.
                    </div>
                  ) : null}
                </fieldset>
              ) : null}

              <label
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "flex-start",
                  fontSize: "var(--font-size-body-small)",
                  lineHeight: 1.4,
                }}
              >
                <input
                  type="checkbox"
                  checked={authorityConfirmed}
                  onChange={(e) => setAuthorityConfirmed(e.target.checked)}
                  disabled={busy}
                  style={{ marginTop: 3 }}
                />
                <span>
                  I confirm that I have the authority to sign this consent — either for myself, or as the legal guardian
                  of the named minor — and that the typed name below is my legal name as it appears on official
                  identification.
                </span>
              </label>

              <label
                style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: "var(--font-size-body-small)" }}
              >
                <span>{age === "guardian_signing" ? "Guardian, your full legal name" : "Your full legal name"}</span>
                <input
                  type="text"
                  value={signedName}
                  onChange={(e) => {
                    if (!profileName) setSignedName(e.target.value);
                  }}
                  readOnly={!!profileName}
                  disabled={busy}
                  placeholder={t("components.consentgate.consent_gate.eg_idris_abdulmumin")}
                  style={{
                    padding: "8px 12px",
                    border: "1px solid var(--color-neutral-border, rgba(0,0,0,0.15))",
                    borderRadius: 6,
                    background: profileName
                      ? "var(--color-neutral-surface, rgba(0,0,0,0.04))"
                      : "var(--color-neutral-background, #fff)",
                    color: "var(--color-neutral-content, #1a1a1a)",
                    fontSize: "var(--font-size-body-small)",
                  }}
                  aria-readonly={!!profileName}
                />
                <span
                  style={{
                    fontSize: "var(--font-size-caption)",
                    color: "var(--color-neutral-content-subtler, #6b6b6b)",
                    lineHeight: 1.4,
                  }}
                >
                  {profileName ? (
                    <>{t("components.consentgate.consent_gate.this_name_comes_from_your_profile_and_is")}</>
                  ) : (
                    <>
                      Your profile doesn't have a name on file. Type your legal name as it appears on official
                      identification — typed pseudonyms weaken the evidentiary value of this signature in a dispute.
                    </>
                  )}
                </span>
              </label>

              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
                {!required ? (
                  <Button variant="neutral" look="outlined" size="small" onClick={onSkip} disabled={busy}>
                    Skip for now
                  </Button>
                ) : null}
                <Button variant="primary" look="filled" size="small" onClick={onSign} waiting={busy} disabled={busy}>
                  Sign and continue
                </Button>
              </div>
            </footer>
          </div>
        </div>
      ) : null}
    </>
  );
};
